'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface DockedChat {
  id: number;
  remoteJid: string;
  name: string;
  avatar?: string | null;
  instanceId?: number | null;
  isMinimized?: boolean;
  callApprovalStatus?: string | null;
}

interface FloatingChatContextType {
  dockedChats: DockedChat[];
  openChat: (chat: {
    id: number;
    remoteJid: string;
    name: string;
    avatar?: string | null;
    instanceId?: number | null;
    callApprovalStatus?: string | null;
  }) => void;
  closeChat: (chatId: number, remoteJid?: string) => void;
  toggleMinimize: (chatId: number, remoteJid?: string) => void;
  isChatOpen: (chatId: number) => boolean;
}

const FloatingChatContext = createContext<FloatingChatContextType | undefined>(undefined);

const MAX_VISIBLE_CHATS = 3;

export function FloatingChatProvider({ children }: { children: ReactNode }) {
  const [dockedChats, setDockedChats] = useState<DockedChat[]>([]);

  const openChat = useCallback((chat: {
    id: number;
    remoteJid: string;
    name: string;
    avatar?: string | null;
    instanceId?: number | null;
    callApprovalStatus?: string | null;
  }) => {
    setDockedChats((prev) => {
      const existingIndex = prev.findIndex((c) => c.id === chat.id || c.remoteJid === chat.remoteJid);
      if (existingIndex > -1) {
        // Chat is already open; restore it from minimized
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          isMinimized: false,
          name: chat.name || updated[existingIndex].name,
          avatar: chat.avatar || updated[existingIndex].avatar,
          callApprovalStatus: chat.callApprovalStatus ?? updated[existingIndex].callApprovalStatus,
        };
        return updated;
      }

      // If at capacity, minimize the oldest unminimized chat
      let newChats = [...prev];
      const unminimizedCount = newChats.filter(c => !c.isMinimized).length;
      if (unminimizedCount >= MAX_VISIBLE_CHATS) {
        // Minimize the first open one
        const firstOpenIndex = newChats.findIndex(c => !c.isMinimized);
        if (firstOpenIndex > -1) {
          newChats[firstOpenIndex] = { ...newChats[firstOpenIndex], isMinimized: true };
        }
      }

      return [
        ...newChats,
        {
          id: chat.id,
          remoteJid: chat.remoteJid,
          name: chat.name,
          avatar: chat.avatar,
          instanceId: chat.instanceId,
          isMinimized: false,
          callApprovalStatus: chat.callApprovalStatus ?? null,
        },
      ];
    });
  }, []);

  const closeChat = useCallback((chatId: number, remoteJid?: string) => {
    setDockedChats((prev) => prev.filter((c) => (chatId ? c.id !== chatId : c.remoteJid !== remoteJid)));
  }, []);

  const toggleMinimize = useCallback((chatId: number, remoteJid?: string) => {
    setDockedChats((prev) =>
      prev.map((c) =>
        (chatId && c.id === chatId) || (remoteJid && c.remoteJid === remoteJid)
          ? { ...c, isMinimized: !c.isMinimized }
          : c
      )
    );
  }, []);

  const isChatOpen = useCallback(
    (chatId: number) => dockedChats.some((c) => c.id === chatId),
    [dockedChats]
  );

  return (
    <FloatingChatContext.Provider
      value={{
        dockedChats,
        openChat,
        closeChat,
        toggleMinimize,
        isChatOpen,
      }}
    >
      {children}
    </FloatingChatContext.Provider>
  );
}

export function useFloatingChat() {
  const context = useContext(FloatingChatContext);
  if (!context) {
    throw new Error('useFloatingChat must be used within a FloatingChatProvider');
  }
  return context;
}
